import { useEffect, useState } from "react";
import { Button } from "reactstrap";
import { omit } from "underscore";

import {
  GDExplorerProps,
  GDRecordProps,
  GrowthDriveProps,
} from "./GrowthDriveInterfaces";
import GalleryPreviewModal from "./LibraryComponents/FileInformation/GalleryPreviewModal";
import TranslateDrawer from "../../components/Records/ImageGallery/TranslateDrawer";
import LibraryToolbar from "../../views/library/LibraryComponents/LibraryToolbar";
import ImageInfoDrawer from "./LibraryComponents/FileInformation/ImageInfoDrawer";
import LibraryFloatingToolbar from "./LibraryComponents/LibraryFloatingToolbar";
import AppTablePagination from "../../components/AppTable/AppTablePagination";
import PreviewSourceRecords from "./LibraryComponents/PreviewSourceRecords";
import AppPageHeader from "../../components/Headers/AppPageHeader";
import ImageCard from "./LibraryComponents/ImageCard/ImageCard";
import useGCDrive from "./LibraryComponents/hooks/useGCDrive";
import { FileInterface } from "../../types/RecordInterfaces";
import SkeletonLoader from "../../elements/SkeletonLoader";
import AppEmptyState from "../../elements/AppEmptyState";
import { FileSources } from "../../types/FileInterfaces";
import { newToastEvent } from "../../utils/ToastUtil";
import { Query } from "../../types/GlobalInterfaces";
import useModule from "../../hooks/useModule";

export type LibraryViewType = "List" | "Grid";
export interface FileRecord {
  recordID: string;
  file: FileInterface;
}

const empty: FileRecord[] = [];

export const defaultSearchFields = [
  "File",
  "Name",
  "Private",
  "Sources",
  "Type",
  "CreatedBy",
  "Created",
  "LastUpdated",
];

function Library(props: GrowthDriveProps) {
  // Basic properties for the Growth Drive component which are shared for all types.
  const {
    withToolbar = true,
    withHeader = true,
    fileType,
    type,
    fileTypes,
    isPrivate,
    filesToFilter,
  } = props;
  //Properties for Reusing Files (Growth Drive Explorer)
  const { onAddFiles, multiFiles } = props as GDExplorerProps;
  // Properties for Image Gallery (Growth Drive Record)
  const { recordID, moduleAPIName, fieldAPIName } = props as GDRecordProps;

  // Growth Drive states
  const [simpleSearchQuery, setSimpleSearchQuery] = useState<any>(null);
  const [simpleSearchValue, setSimpleSearchValue] = useState("");
  const [viewType, setViewType] = useState<LibraryViewType>("List");
  const [selectedFiles, setSelectedFiles] = useState<FileRecord[]>(empty);
  const [openInfo, setOpenInfo] = useState<FileRecord | null>(null);
  const [openPreview, setPreview] = useState<number | null>(null);
  const [fileToTranslate, setFileToTranslate] = useState<FileInterface | null>(
    null
  );
  const [query, setQuery] = useState<Query>({
    Fields: defaultSearchFields,
    Filters: [],
    OrderBy: {
      Field: "Created",
      Direction: "DESC",
    },
    Pagination: {
      Count: 25,
      Page: 1,
    },
  });
  const [error, setError] = useState<{
    FileID: string;
    FileName: string;
    Sources: any[];
  } | null>(null);

  const { module } = useModule("Files");
  const {
    records,
    pagination,
    queryError,
    updatingFile,
    recordsLoading,
    handleDelete,
    handleUpdateSources,
    handleRecordFileCapture,
    handleUpdateComponent,
    handleIndexFiles,
    handleTranscribe,
  } = useGCDrive({
    query: simpleSearchQuery || query,
    fileType: fileType,
    fileTypes: fileTypes,
    isPrivate: isPrivate,
    filesToFilter,
    parentRecordID: recordID,
    moduleAPIName,
    fieldAPIName,
  });

  function handleFileUpdate(
    value: string,
    key: "Name" | "Caption" | "Transcript",
    onSuccess?: () => void
  ) {
    const recordID =
      openInfo?.recordID ||
      (typeof openPreview === "number" ? records?.[openPreview]?.id : null);
    const file =
      openInfo?.file ||
      (typeof openPreview === "number" ? records?.[openPreview]?.File : null);

    if (!file || !recordID) return;

    let filePayload = {
      [key]: value,
    };

    handleUpdateComponent({
      type: "File",
      componentID: recordID,
      payload: filePayload,
      onSuccess: onSuccess,
    });
  }

  useEffect(() => {
    if (!onAddFiles) return;

    let files = selectedFiles.map((f) => {
      const fileMetadata = omit(f.file, [
        "URL",
        "CreatedBy",
        "LastUpdatedBy",
        "Tags",
        "_ModuleAPIName",
        "_SubitemParentAPIName",
        "_RecordName",
      ]);

      return {
        ...fileMetadata,
        RecordID: f.recordID,
        UploadedBy:
          (typeof fileMetadata.UploadedBy === "object"
            ? fileMetadata.UploadedBy.id
            : fileMetadata.UploadedBy) ?? "",
      };
    });

    onAddFiles(files);
  }, [selectedFiles]);

  function handleFileDeletion(fileID: string) {
    const fileRecord = records?.find((r: any) => r.id === fileID);

    if (!!fileRecord?.Sources?.length && type !== "Growth Drive Record") {
      newToastEvent(
        "warning",
        <div className="tw:flex tw:items-center">
          <div>
            This file is being used by other records, you cannot delete it.
          </div>
          <Button
            className="tw:w-42"
            size="sm"
            color="primary"
            onClick={() => {
              setError({
                FileName: fileRecord.File.Name,
                Sources: fileRecord.Sources,
                FileID: fileRecord?.id || "",
              });
            }}
          >
            View Records
          </Button>
        </div>,
        "ConflictFileDeletion",
        {
          autoClose: true,
          loading: false,
        }
      );

      return;
    }

    handleDelete(fileID, "File");
  }

  return (
    <>
      <div className="tw:flex tw:flex-col tw:w-full">
        {withHeader && (
          <div className="tw:px-5">
            <AppPageHeader
              title="Growth Drive"
              description="Browse and search through your files."
            />
          </div>
        )}
        {withToolbar && (
          <div className="tw:px-5">
            <LibraryToolbar
              query={query}
              setQuery={setQuery}
              setSimpleSearchQuery={setSimpleSearchQuery}
              simpleSearchQuery={simpleSearchQuery}
              setSimpleSearchValue={setSimpleSearchValue}
              simpleSearchValue={simpleSearchValue}
              module={module!}
              setViewType={setViewType}
              viewType={viewType}
              fileFieldPrivacySetting={isPrivate}
              fromGCRecord={type === "Growth Drive Record"}
              onIndexFiles={
                type === "Growth Drive Record" && records?.length
                  ? handleIndexFiles
                  : undefined
              }
              handleRecordFileCapture={handleRecordFileCapture}
              filesToFilter={
                type === "Growth Drive Record"
                  ? records.flatMap((r) =>
                      r.File ? [{ ...r.File, RecordID: r._id || r.id }] : []
                    )
                  : filesToFilter
              }
            />
          </div>
        )}
        {!!records?.length ? (
          <div className="tw:flex-1 tw:overflow-y-auto tw:px-5">
            <div className="tw:flex tw:flex-wrap tw:my-6 tw:md:justify-start tw:justify-center tw:gap-7">
              {records?.map((r: any, index: number) => {
                const file = r?.File;

                return (
                  <ImageCard
                    file={file}
                    fileID={r._id}
                    setPreview={setPreview}
                    setOpenInfo={setOpenInfo}
                    handleTranscribe={handleTranscribe}
                    deleteClick={(data) => handleFileDeletion(data?.recordID)}
                    setFileToTranslate={
                      type === "Growth Drive Record"
                        ? setFileToTranslate
                        : undefined
                    }
                    selectedItems={selectedFiles}
                    viewType={viewType}
                    onSelect={setSelectedFiles}
                    index={index}
                    multiFiles={multiFiles}
                  />
                );
              })}
            </div>
          </div>
        ) : recordsLoading ? (
          <LibraryLoading viewType={viewType} />
        ) : (
          !records?.length &&
          !recordsLoading && (
            <div className="tw:p-5">
              <AppEmptyState
                icon="mdi-image-multiple-outline"
                title="No Content"
                description="No files have been added yet"
              />
            </div>
          )
        )}
        {!!records?.length && (
          <div
            className={`gc-table-pagination-sticky text-center text-sm-left pr-0 tw:w-full`}
          >
            <AppTablePagination
              records={records}
              pagination={pagination}
              query={query}
              setQuery={setQuery}
              error={queryError}
              setSelectedRecords={() => {
                setSelectedFiles([]);
              }}
              loading={recordsLoading}
              module={module}
              containerCSS={!withHeader ? undefined : "pr-6"}
            />
          </div>
        )}
        {!!selectedFiles?.length && !onAddFiles && (
          <LibraryFloatingToolbar
            selectedFiles={selectedFiles}
            onClear={() => setSelectedFiles([])}
          />
        )}
        {openInfo && (
          <ImageInfoDrawer
            file={openInfo?.file}
            open={!!openInfo && openPreview === null}
            parentRecordID={""}
            onClose={() => setOpenInfo(null)}
            record={null}
            submitting={updatingFile}
            onNameUpdate={(value: any, onSuccess) =>
              handleFileUpdate(value?.Name ?? "", "Name", onSuccess)
            }
          />
        )}
        <GalleryPreviewModal
          setSelected={setPreview}
          deleteFile={() => {
            let record = openPreview !== null ? records?.[openPreview] : null;

            if (!record || !record?.id) return;

            handleFileDeletion(record?.id);
          }}
          submitting={updatingFile}
          handleUpdateFile={(value, key) => handleFileUpdate(value, key)}
          isOpen={typeof openPreview === "number"}
          selected={openPreview!}
          closeModal={() => setPreview(null)}
          files={records}
          nextFile={() =>
            setPreview((prev) => (typeof prev === "number" ? prev + 1 : null))
          }
          previousFile={() =>
            setPreview((prev) => (typeof prev === "number" ? prev - 1 : null))
          }
          record={null}
          onExited={() => setPreview(null)}
          setOpenInfo={setOpenInfo}
          openInfo={openInfo}
        />
      </div>
      <PreviewSourceRecords
        open={!!error}
        onClose={() => setError(null)}
        fileName={error?.FileName || ""}
        sources={error?.Sources || []}
        handleSourcesUpdate={(sources: FileSources[]) =>
          handleUpdateSources({
            fileID: error?.FileID ?? "",
            sources,
          })
        }
      />
      <TranslateDrawer
        open={!!fileToTranslate}
        onClose={() => setFileToTranslate(null)}
        file={fileToTranslate}
        handleCapture={(fileData, fileName, onSuccess: any) =>
          handleRecordFileCapture({
            type: "Custom",
            // @ts-ignore
            target: { files: fileData.files },
            onSuccess: onSuccess && onSuccess,
          })
        }
        submitting={updatingFile}
      />
    </>
  );
}

export default Library;

function LibraryLoading({ viewType }: { viewType: LibraryViewType }) {
  return (
    <div className="tw:flex-1 tw:overflow-y-auto tw:px-5">
      <div className="tw:flex tw:flex-wrap tw:my-6 tw:md:justify-start tw:justify-center tw:gap-7">
        {new Array(15).fill(0).map((_) => {
          if (viewType === "Grid") {
            return <SkeletonLoader css="tw:w-52.5 tw:h-45" />;
          } else {
            return (
              <SkeletonLoader css="tw:w-full tw:md:w-70 tw:lg:w-76 tw:xl:w-88 tw:h-[100px]" />
            );
          }
        })}
      </div>
    </div>
  );
}
